import { useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { CustomerSummary } from '../api/types'
import { Card, Empty, Field, Note, PageHead } from '../components/ui'
import { useAuth } from '../auth/AuthContext'
import { useApi } from '../hooks/useApi'
import { searchNames } from '../lib/search'

const SEARCH_APPEARS_AFTER = 8

export function Customers() {
  const { isOwner } = useAuth()
  const customers = useApi<CustomerSummary[]>('/customers')

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)

  const rows = searchNames(customers.data ?? [], query)

  async function add(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)
    try {
      await api.post('/customers', { name, phone: phone || null })
      setName('')
      setPhone('')
      customers.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function savePhone(customer: CustomerSummary, newPhone: string) {
    if ((customer.phone ?? '') === newPhone) return
    try {
      await api.put(`/customers/${customer.id}`, { name: customer.name, phone: newPhone || null })
      customers.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  /**
   * Removing a name takes two taps, and the server refuses outright unless the
   * balance is exactly zero. A name with money against it is never one tap from
   * gone.
   */
  async function remove(customer: CustomerSummary) {
    setError(null)
    try {
      await api.delete(`/customers/${customer.id}`)
      setDone(`${customer.name} اور اُن کی ساری انٹریاں ہٹا دی گئیں۔`)
      setConfirming(null)
      customers.reload()
    } catch (err) {
      setError(errorMessage(err))
      setConfirming(null)
    }
  }

  return (
    <>
      <PageHead title="گاہک" hint="نام اور فون نمبر۔" />

      {error && <Note kind="error">{error}</Note>}
      {done && <Note kind="info">{done}</Note>}

      <Card title="نیا نام">
        <form onSubmit={add} className="row">
          <Field label="نام">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          <Field label="فون (ضروری نہیں)">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={30}
              inputMode="tel"
            />
          </Field>
          <div className="field">
            <label>&nbsp;</label>
            <button className="btn" disabled={!name.trim()}>
              نام لکھیں
            </button>
          </div>
        </form>
      </Card>

      <Card
        title={`لسٹ (${customers.data?.length ?? 0})`}
        actions={
          (customers.data?.length ?? 0) > SEARCH_APPEARS_AFTER ? (
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="نام ڈھونڈیں"
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cfc9c0' }}
            />
          ) : undefined
        }
      >
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>نام</th>
                <th>فون</th>
                {isOwner && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.name}</td>
                  <td>
                    <input
                      defaultValue={customer.phone ?? ''}
                      onBlur={(e) => savePhone(customer, e.target.value.trim())}
                      placeholder="—"
                      inputMode="tel"
                      style={{
                        border: '1px solid transparent',
                        background: 'transparent',
                        padding: '4px 6px',
                        borderRadius: 6,
                        font: 'inherit',
                        direction: 'ltr',
                        textAlign: 'right',
                        width: 150,
                      }}
                    />
                  </td>
                  {isOwner && (
                    <td>
                      <div className="row-actions">
                        {confirming === customer.id ? (
                          <>
                            <button
                              type="button"
                              className="btn btn--danger btn--small"
                              onClick={() => remove(customer)}
                            >
                              پکا ڈیلیٹ
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--small"
                              onClick={() => setConfirming(null)}
                            >
                              رہنے دیں
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--danger btn--small"
                            onClick={() => setConfirming(customer.id)}
                          >
                            ڈیلیٹ
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <Empty>کوئی نام نہیں ملا۔</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {isOwner && (
          <p className="field__hint" style={{ marginTop: 10 }}>
            نام صرف اُسی وقت ہٹ سکتا ہے جب بقایا بالکل صفر ہو۔ نام کے ساتھ اُس کی ساری انٹریاں بھی
            ہٹ جاتی ہیں۔
          </p>
        )}
      </Card>
    </>
  )
}
